import { parseBackup, sampleBackup, type Person } from '@lineage/core';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ProfilePanel } from '../person/ProfilePanel';
import { PersonPicker } from '../perspective/Picker';
import { SamplePhotos } from './PersonCard';
import { samplePortraitUrl } from './samplePortraits';
import { useTreeModel } from './model';
import { useView } from './store';
import { TreeCanvas } from './TreeCanvas';
import s from './TreePage.module.css';
import { useT } from '../../i18n';

/** A read-only sample family that works without an account: the same canvas and labels as the real thing. */
export function DemoPage() {
  const t = useT();
  const data = useMemo(() => parseBackup(sampleBackup()), []);
  const portraits = useMemo(
    () => new Map(Object.entries(data.idMap).map(([key, id]) => [id, samplePortraitUrl(key)])),
    [data],
  );
  const [meId, setMeId] = useState<string | null>(data.meId);
  const [seeAs, setSeeAs] = useState(false);
  const snap = useMemo(
    () => ({ people: data.people, parentLinks: data.parentLinks, partnerships: data.partnerships, version: 1 }),
    [data],
  );
  const model = useTreeModel(snap, meId);
  const selectedId = useView((v) => v.selectedId);
  const selected: Person | undefined = selectedId ? model.graph.people.get(selectedId) : undefined;
  return (
    <SamplePhotos.Provider value={portraits}>
      <div className={s.shell}>
        <header className={s.header}>
          <Link to="/" className="btn btn-ghost" aria-label={t.common.back}>
            ←
          </Link>
          <h1 className={s.title}>
            {t.tree.sampleName} <span className="pill">{t.tree.sample}</span>
          </h1>
          <button className="btn btn-ghost" type="button" aria-label={t.tree.seeAs} onClick={() => setSeeAs(true)}>
            {t.tree.viewingAs(model.graph.people.get(meId ?? '')?.firstName ?? '…')}
          </button>
        </header>
        <div className={s.stage}>
          <TreeCanvas
            model={model}
            meId={meId}
            frame={window.innerWidth >= 900 ? 'fit' : 'auto'}
            focusY={selected && window.innerWidth <= 800 ? 0.22 : 0.5}
          />
          {selected && (
            <ProfilePanel
              person={selected}
              graph={model.graph}
              labels={model.labels}
              pack={model.pack}
              kin={model.kins.get(selected.id)}
              isMe={selected.id === meId}
              isLinkedToAnAccount={false}
              onSelect={(id) => useView.getState().select(id)}
              onClose={() => useView.getState().select(null)}
              actions={{
                canEdit: false,
              canModify: false,
                canInvite: false,
                signedIn: true,
                onSeeAs: () => setMeId(selected.id),
                onThisIsMe: () => setMeId(selected.id),
              }}
            />
          )}
        </div>
        {seeAs && (
          <PersonPicker
            title={t.tree.seeAs}
            people={snap.people}
            onPick={(id) => {
              setMeId(id);
              setSeeAs(false);
            }}
            onClose={() => setSeeAs(false)}
          />
        )}
      </div>
    </SamplePhotos.Provider>
  );
}
